import { useRef, useCallback } from 'react';
import { addMinutes, differenceInMinutes } from 'date-fns';
import { INTERVAL_MINUTES } from '@/components/shifts/constants';

interface UseShiftResizeOptions {
  startTime: Date;
  endTime: Date;
  intervalWidthPx: number; // Width of one INTERVAL_MINUTES interval in pixels
  minDurationMinutes?: number;
  onResize: (newStart: Date, newEnd: Date) => void;
  onResizeEnd: (newStart: Date, newEnd: Date) => void;
}

interface ResizeState {
  isActive: boolean;
  edge: 'left' | 'right' | null;
  startX: number;
  originalStartTime: Date;
  originalEndTime: Date;
  currentStartTime: Date;
  currentEndTime: Date;
}

/**
 * useShiftResize - Robust resize hook for shift components
 *
 * Design principles:
 * 1. All drag state in refs - no useState during drag to avoid re-renders
 * 2. Options stored in ref to avoid stale closures
 * 3. Correct formula: INTERVAL_MINUTES / intervalWidthPx for minutes-per-pixel
 * 4. Pointer Events API for reliable tracking
 * 5. Self-contained listener cleanup
 */
export function useShiftResize(options: UseShiftResizeOptions) {
  const { minDurationMinutes = INTERVAL_MINUTES } = options;

  // All mutable state in a single ref - NO useState during drag
  const stateRef = useRef<ResizeState>({
    isActive: false,
    edge: null,
    startX: 0,
    originalStartTime: options.startTime,
    originalEndTime: options.endTime,
    currentStartTime: options.startTime,
    currentEndTime: options.endTime,
  });

  // Store latest options in ref to avoid stale closures
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Pure function: Convert pixel delta to time delta
  const pixelDeltaToMinutes = useCallback((deltaX: number): number => {
    const { intervalWidthPx } = optionsRef.current;
    if (!intervalWidthPx || intervalWidthPx <= 0) return 0;

    // CORRECT FORMULA: Each interval is INTERVAL_MINUTES (15 min)
    const minutesPerPixel = INTERVAL_MINUTES / intervalWidthPx;
    const rawMinutes = deltaX * minutesPerPixel;
    // Snap to interval boundaries
    return Math.round(rawMinutes / INTERVAL_MINUTES) * INTERVAL_MINUTES;
  }, []);

  // Handle pointer/mouse move - uses refs only, no state
  const handleMove = useCallback((clientX: number) => {
    const state = stateRef.current;
    if (!state.isActive || !state.edge) return;

    const deltaX = clientX - state.startX;
    const deltaMinutes = pixelDeltaToMinutes(deltaX);

    let newStart = state.originalStartTime;
    let newEnd = state.originalEndTime;

    if (state.edge === 'left') {
      newStart = addMinutes(state.originalStartTime, deltaMinutes);
      // Ensure minimum duration
      if (differenceInMinutes(newEnd, newStart) < minDurationMinutes) {
        newStart = addMinutes(newEnd, -minDurationMinutes);
      }
    } else {
      newEnd = addMinutes(state.originalEndTime, deltaMinutes);
      // Ensure minimum duration
      if (differenceInMinutes(newEnd, newStart) < minDurationMinutes) {
        newEnd = addMinutes(newStart, minDurationMinutes);
      }
    }

    // Update ref state
    state.currentStartTime = newStart;
    state.currentEndTime = newEnd;

    // Notify parent for visual update
    optionsRef.current.onResize(newStart, newEnd);
  }, [pixelDeltaToMinutes, minDurationMinutes]);

  // Handle drag end
  const handleEnd = useCallback(() => {
    const state = stateRef.current;
    if (!state.isActive) return;

    state.isActive = false;
    const edge = state.edge;
    state.edge = null;

    // Only commit if we actually had an edge (valid resize)
    if (edge) {
      optionsRef.current.onResizeEnd(state.currentStartTime, state.currentEndTime);
    }
  }, []);

  // Start resize - attaches document listeners
  const startResize = useCallback((e: React.PointerEvent | React.MouseEvent, edge: 'left' | 'right') => {
    e.preventDefault();
    e.stopPropagation();

    const { startTime, endTime } = optionsRef.current;

    // Initialize state
    stateRef.current = {
      isActive: true,
      edge,
      startX: e.clientX,
      originalStartTime: startTime,
      originalEndTime: endTime,
      currentStartTime: startTime,
      currentEndTime: endTime,
    };

    // Use pointer capture if available (more reliable)
    if ('setPointerCapture' in e.currentTarget && e.nativeEvent instanceof PointerEvent) {
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.nativeEvent.pointerId);
      } catch {
        // Pointer capture may fail in some scenarios, continue without it
      }
    }

    // Document listeners for when pointer leaves element
    const onDocumentMove = (moveEvent: MouseEvent | PointerEvent) => {
      handleMove(moveEvent.clientX);
    };

    const onDocumentUp = () => {
      handleEnd();
      document.removeEventListener('mousemove', onDocumentMove);
      document.removeEventListener('mouseup', onDocumentUp);
      document.removeEventListener('pointermove', onDocumentMove);
      document.removeEventListener('pointerup', onDocumentUp);
    };

    document.addEventListener('mousemove', onDocumentMove);
    document.addEventListener('mouseup', onDocumentUp);
    document.addEventListener('pointermove', onDocumentMove);
    document.addEventListener('pointerup', onDocumentUp);
  }, [handleMove, handleEnd]);

  // Cancel resize without committing
  const cancelResize = useCallback(() => {
    const state = stateRef.current;
    if (!state.isActive) return;

    state.isActive = false;
    state.edge = null;

    // Reset to original times
    optionsRef.current.onResize(state.originalStartTime, state.originalEndTime);
  }, []);

  return {
    startResize,
    cancelResize,
    isResizing: () => stateRef.current.isActive,
    getResizeEdge: () => stateRef.current.edge,
  };
}
